import React, { useMemo, useState } from 'react';
import { WidgetLazyLoader } from 'components/common/WidgetLazyLoader';
import { ignoreRedirect } from 'core/services/redirectService';
import { configuration } from 'config/constants';
import { DefaultTooltip } from '../tooltip/Tooltip';
import { uniqueId } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { handleA11y } from 'core/utils/a11y';
import { useMediaBreakpoint } from 'components/hooks/media/useMediaBreakpoint';
import { useLoggerTracking } from 'components/hooks/logger/useLoggerTracking';
import { TrackedFeature, TrackedPage } from 'services/Logger';
import { TRACKING_PARAM_KEYS } from 'services/Logger';

declare global {
    namespace React {
        namespace JSX {
            interface IntrinsicElements {
                'sgmarkets-request-access': {
                    url: string;
                    onClick: (event: React.MouseEvent | React.KeyboardEvent) => void;
                    onKeyUp: (event: React.MouseEvent | React.KeyboardEvent) => void;
                    site: string;
                    classBtn: string;
                    'use-icon': boolean;
                    id: string;
                    'sg-bootstrap': string;
                };
            }
        }
    }
}

type RequestAccessProps = {
    serviceKey: string;
    name?: Nullable<string>;
    url: string;
    buttonClass?: string;
    useIcon?: boolean;
    trackedPage?: TrackedPage;
};

export const RequestAccess: React.FunctionComponent<RequestAccessProps> = ({
    serviceKey,
    name,
    url,
    buttonClass,
    useIcon,
    trackedPage = TrackedPage.ServiceDetail,
}) => {
    const { t: translate } = useTranslation('tooltip');
    const mediaBreakpoint = useMediaBreakpoint();
    const { sharedCdnUrl } = configuration;
    const track = useLoggerTracking();
    const siteConfig = JSON.stringify({
        url,
        serviceKey,
        name,
    });

    const showTooltip = ['xs', 'sm'].includes(mediaBreakpoint);

    const buttonId = useMemo(
        () => (useIcon ? uniqueId('requestaccessTarget') : ''),
        [useIcon],
    );

    const onRequestAccess = (event: React.MouseEvent | React.KeyboardEvent) => {
        ignoreRedirect(event);
        track(trackedPage, TrackedFeature.OpenServiceRequest, { [TRACKING_PARAM_KEYS.page]: window.location.pathname, [TRACKING_PARAM_KEYS.serviceKey]: serviceKey });
    };

    // "?v=today" only to force widget refresh
    const [today] = useState(() => new Date().toLocaleDateString());
    const requestAccessJs = `${sharedCdnUrl}/sgr-widgets/sgmarkets-request-access/sgmarkets-request-access.js?v=${today}`;

    return <WidgetLazyLoader script={requestAccessJs}>
        <DefaultTooltip text={translate('tooltip:requestAccess')} enabled={!showTooltip}>
            <sgmarkets-request-access
                url={url}
                onClick={handleA11y(onRequestAccess)}
                onKeyUp={handleA11y(onRequestAccess)}
                site={siteConfig}
                classBtn={`btn btn-lg ${buttonClass || 'btn-flat-socgen'}`}
                use-icon={useIcon ?? false}
                id={buttonId}
                sg-bootstrap="5"
            />
        </DefaultTooltip>
    </WidgetLazyLoader>;
};
